import { useEffect, useRef, useState } from 'react';
import { gameSounds } from '../../lib/gameSounds';

interface TargetWord {
  word: string;
  length: number;
  points: number;
  foundBy: string | null;
  foundAt: number | null;
}

interface FoundEntry {
  word: string;
  finder: string;
  points: number;
  at: number;
}

interface ChatWordsLastFind {
  viewer: string;
  word: string;
  points: number;
  ts: number;
}

interface ChatWordsState {
  baseWord: string;
  letters: string[];
  hiddenIndices: number[];
  fakeIndices: number[];
  targetWords: TargetWord[];
  found: FoundEntry[];
  scores: Record<string, number>;
  lockedUntil: Record<string, number>;
  level: number;
  levelGoalPoints: number;
  levelTotalPoints: number;
  levelScore: number;
  levelEndsAt: number;
  sectionEndsAt: number;
  sectionsElapsed?: number;
  firstLetterHintsRevealed?: boolean;
  lastFind?: ChatWordsLastFind | null;
  finished: boolean;
  stars: number;
  transitioningUntil: number | null;
  lastLevel: { level: number; stars: number; score: number; total: number } | null;
}

/**
 * ChatWords overlay — redesigned to match Hype Million's gameshow shell
 * (stage spotlight, brand block + chips header, timer ring, big rounded
 * panels, option-pod styling) but in cyan/teal instead of gold so the two
 * games read as cousins, not twins.
 */

const PALETTE = {
  bg: '#020617',
  bgInner: '#0b2030',
  accent: '#06b6d4', // cyan-500
  accentLight: '#67e8f9', // cyan-300
  accentDeep: '#0e7490', // cyan-700
  accentBorder: 'rgba(6,182,212,0.55)',
  accentBorderSoft: 'rgba(6,182,212,0.35)',
  accentGlow: 'rgba(6,182,212,0.45)',
  green: '#22c55e',
  red: '#ef4444',
  text: '#fff',
  textDim: 'rgba(255,255,255,0.65)',
};

const ANIMATIONS = `
@keyframes cw-fadeIn { from { opacity: 0 } to { opacity: 1 } }
@keyframes cw-spotlight { 0% { opacity: 0.3 } 50% { opacity: 0.55 } 100% { opacity: 0.3 } }
@keyframes cw-pulse { 0%, 100% { transform: scale(1) } 50% { transform: scale(1.04) } }
@keyframes cw-rise { from { transform: translateY(20px); opacity: 0 } to { transform: translateY(0); opacity: 1 } }
@keyframes cw-tile-pulse { 0%, 100% { opacity: 0.55 } 50% { opacity: 1 } }
@keyframes cw-banner-in { 0% { transform: translate(-50%, -60%) scale(0.6); opacity: 0 } 100% { transform: translate(-50%, -50%) scale(1); opacity: 1 } }
@keyframes cw-banner-out { 0% { opacity: 1 } 100% { opacity: 0; transform: translate(-50%, -40%) scale(1.1) } }
@keyframes cw-star-pop { 0% { transform: scale(0.4); opacity: 0 } 70% { transform: scale(1.2); opacity: 1 } 100% { transform: scale(1); opacity: 1 } }
`;

export function ChatWordsBody({ state }: { state: ChatWordsState; config: unknown }) {
  const grouped = groupByLength(state.targetWords);
  const top = topScores(state.scores, 8);

  const prevFindTsRef = useRef<number>(0);
  const prevFoundCountRef = useRef<number>(state.found.length);
  const prevFinishedRef = useRef<boolean>(false);
  const prevLevelRef = useRef<number>(state.level);
  const [findBanner, setFindBanner] = useState<ChatWordsLastFind | null>(null);

  useEffect(() => {
    if (state.found.length > prevFoundCountRef.current) {
      void gameSounds.play('correct');
    }
    prevFoundCountRef.current = state.found.length;
  }, [state.found.length]);

  useEffect(() => {
    const lf = state.lastFind;
    if (!lf || lf.ts <= prevFindTsRef.current) return;
    prevFindTsRef.current = lf.ts;
    setFindBanner(lf);
    void gameSounds.play('reveal');
    const t = setTimeout(() => setFindBanner(null), 2200);
    return () => clearTimeout(t);
  }, [state.lastFind]);

  useEffect(() => {
    if (state.finished && !prevFinishedRef.current) {
      void gameSounds.play('loser');
    }
    prevFinishedRef.current = state.finished;
  }, [state.finished]);

  useEffect(() => {
    if (state.level !== prevLevelRef.current) {
      void gameSounds.play('game_start');
      prevLevelRef.current = state.level;
    }
  }, [state.level]);

  if (state.finished) {
    return <Finished state={state} top={top.slice(0, 5)} />;
  }
  if (state.transitioningUntil && state.lastLevel) {
    return <LevelReveal lastLevel={state.lastLevel} nextLevel={state.level} />;
  }

  return (
    <div style={stageStyle}>
      <style>{ANIMATIONS}</style>
      <Spotlight />

      <Header
        level={state.level}
        score={state.levelScore}
        goal={state.levelGoalPoints}
        total={state.levelTotalPoints}
        foundCount={state.found.length}
        totalSlots={state.targetWords.length}
        levelEndsAt={state.levelEndsAt}
      />

      <main style={mainStyle}>
        <CommandStripe />
        {state.found.length > 0 && <RecentTicker found={state.found.slice(-6)} />}
        <LetterPool letters={state.letters} hiddenIndices={state.hiddenIndices} />
        <SlotsArea grouped={grouped} showFirstLetters={!!state.firstLetterHintsRevealed} />
        <Footer sectionEndsAt={state.sectionEndsAt} levelEndsAt={state.levelEndsAt} />
      </main>

      <TopChattersRail top={top} />

      {findBanner && <FindBanner event={findBanner} />}
    </div>
  );
}

/* ───────────────────── stage / shell ───────────────────── */

const stageStyle: React.CSSProperties = {
  position: 'relative',
  flex: 1,
  display: 'grid',
  gridTemplateColumns: '1fr 320px',
  gridTemplateRows: 'auto 1fr',
  gap: 28,
  padding: '32px 40px',
  background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
  color: PALETTE.text,
  fontFamily:
    '"Rajdhani","Orbitron",ui-sans-serif,system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif',
  overflow: 'hidden',
  animation: 'cw-fadeIn 400ms ease both',
};

const mainStyle: React.CSSProperties = {
  gridColumn: '1 / 2',
  gridRow: '2 / 3',
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  minHeight: 0,
};

function Spotlight() {
  return (
    <>
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(6,182,212,0.20) 0%, transparent 55%)',
          animation: 'cw-spotlight 6s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background:
            'radial-gradient(ellipse at 0% 100%, rgba(14,116,144,0.12) 0%, transparent 55%)',
          pointerEvents: 'none',
        }}
      />
    </>
  );
}

/* ───────────────────── header ───────────────────── */

function Header({
  level,
  score,
  goal,
  total,
  foundCount,
  totalSlots,
  levelEndsAt,
}: {
  level: number;
  score: number;
  goal: number;
  total: number;
  foundCount: number;
  totalSlots: number;
  levelEndsAt: number;
}) {
  return (
    <header
      style={{
        gridColumn: '1 / -1',
        gridRow: '1 / 2',
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 18,
        animation: 'cw-rise 500ms ease both',
      }}
    >
      <Brand level={level} />
      <ChipRow
        score={score}
        goal={goal}
        total={total}
        foundCount={foundCount}
        totalSlots={totalSlots}
      />
      <TimerRing endsAt={levelEndsAt} />
    </header>
  );
}

function Brand({ level }: { level: number }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div
        style={{
          width: 60,
          height: 60,
          borderRadius: 12,
          background: `linear-gradient(135deg, ${PALETTE.accent}, ${PALETTE.accentDeep})`,
          display: 'grid',
          placeItems: 'center',
          fontSize: 30,
          color: '#001218',
          fontWeight: 900,
          fontFamily: '"Orbitron",sans-serif',
          boxShadow: `0 8px 28px ${PALETTE.accentGlow}`,
        }}
      >
        A
      </div>
      <div>
        <div
          style={{
            fontSize: 12,
            letterSpacing: 4,
            color: PALETTE.accentLight,
            fontWeight: 700,
            textTransform: 'uppercase',
          }}
        >
          Chat
        </div>
        <div
          style={{
            fontSize: 26,
            fontWeight: 900,
            letterSpacing: 3,
            lineHeight: 1,
            fontFamily: '"Orbitron","Rajdhani",sans-serif',
          }}
        >
          WORDS
        </div>
        <div
          style={{
            fontSize: 10,
            letterSpacing: 2,
            color: PALETTE.textDim,
            marginTop: 3,
          }}
        >
          Level {level}
        </div>
      </div>
    </div>
  );
}

function ChipRow({
  score,
  goal,
  total,
  foundCount,
  totalSlots,
}: {
  score: number;
  goal: number;
  total: number;
  foundCount: number;
  totalSlots: number;
}) {
  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 12,
      }}
    >
      <Chip label="Score" value={`${score} / ${total}`} />
      <Chip label="Goal" value={String(goal)} highlight />
      <Chip label="Found" value={`${foundCount} / ${totalSlots}`} />
    </div>
  );
}

function Chip({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <div
      style={{
        padding: '10px 18px',
        background: 'rgba(255,255,255,0.05)',
        border: `1px solid ${highlight ? PALETTE.accentBorder : PALETTE.accentBorderSoft}`,
        borderRadius: 999,
        backdropFilter: 'blur(8px)',
        boxShadow: highlight ? `0 0 24px -8px ${PALETTE.accentGlow}` : undefined,
      }}
    >
      <div
        style={{
          fontSize: 10,
          textTransform: 'uppercase',
          letterSpacing: 2,
          color: PALETTE.textDim,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          color: highlight ? PALETTE.accentLight : PALETTE.text,
          fontFamily: '"JetBrains Mono","Orbitron",monospace',
        }}
      >
        {value}
      </div>
    </div>
  );
}

function TimerRing({ endsAt }: { endsAt: number }) {
  const [secondsLeft, setSecondsLeft] = useState(() =>
    Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)),
  );
  useEffect(() => {
    const tick = () => setSecondsLeft(Math.max(0, Math.ceil((endsAt - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [endsAt]);
  const totalSeconds = Math.max(secondsLeft, 30);
  const pct = Math.max(0, Math.min(1, secondsLeft / totalSeconds));
  const urgent = secondsLeft <= 10;
  const r = 32;
  const c = 2 * Math.PI * r;
  return (
    <div
      style={{
        position: 'relative',
        width: 88,
        height: 88,
        display: 'grid',
        placeItems: 'center',
        animation: urgent ? 'cw-pulse 600ms ease-in-out infinite' : undefined,
      }}
    >
      <svg
        width={88}
        height={88}
        style={{ position: 'absolute', inset: 0, transform: 'rotate(-90deg)' }}
      >
        <circle cx={44} cy={44} r={r} stroke="rgba(255,255,255,0.10)" strokeWidth={5} fill="none" />
        <circle
          cx={44}
          cy={44}
          r={r}
          stroke={urgent ? PALETTE.red : PALETTE.accent}
          strokeWidth={5}
          fill="none"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset 500ms linear' }}
        />
      </svg>
      <div
        style={{
          fontSize: 26,
          fontWeight: 900,
          color: urgent ? PALETTE.red : PALETTE.accentLight,
          fontFamily: '"Orbitron",sans-serif',
        }}
      >
        {secondsLeft}
      </div>
    </div>
  );
}

/* ───────────────────── command stripe + ticker ───────────────────── */

function CommandStripe() {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        padding: '12px 18px',
        background:
          'linear-gradient(90deg, rgba(6,182,212,0.10), rgba(6,182,212,0.18), rgba(6,182,212,0.10))',
        border: `1px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 14,
        fontSize: 16,
        fontWeight: 600,
        color: PALETTE.textDim,
      }}
    >
      <span style={{ color: PALETTE.text }}>Just</span>
      <span
        style={{
          fontFamily: '"JetBrains Mono",monospace',
          padding: '4px 10px',
          background: 'rgba(34,197,94,0.18)',
          border: `1px solid ${PALETTE.green}`,
          borderRadius: 8,
          color: '#6ee7b7',
          fontWeight: 800,
          letterSpacing: 1,
        }}
      >
        say a word
      </span>
      <span>in chat to claim a slot</span>
    </div>
  );
}

function RecentTicker({ found }: { found: FoundEntry[] }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '8px 14px',
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <span
        style={{
          fontSize: 10,
          letterSpacing: 3,
          color: PALETTE.accentLight,
          fontFamily: '"Orbitron",sans-serif',
          fontWeight: 800,
        }}
      >
        RECENT
      </span>
      <div style={{ display: 'flex', gap: 16, overflow: 'hidden', whiteSpace: 'nowrap', flex: 1 }}>
        {[...found].reverse().map((f, i) => (
          <span
            key={`${f.word}-${f.at}-${i}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}
          >
            <span style={{ color: '#6ee7b7', fontWeight: 700 }}>{f.finder}</span>
            <span
              style={{
                fontFamily: '"JetBrains Mono",monospace',
                fontWeight: 800,
                letterSpacing: 1,
                color: PALETTE.text,
              }}
            >
              {f.word.toUpperCase()}
            </span>
            <span
              style={{
                fontFamily: '"JetBrains Mono",monospace',
                fontSize: 11,
                color: '#34d399',
              }}
            >
              +{f.points}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ───────────────────── letter pool ───────────────────── */

function LetterPool({ letters, hiddenIndices }: { letters: string[]; hiddenIndices: number[] }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 12,
        padding: '12px 0',
      }}
    >
      {letters.map((ch, i) => {
        const hidden = hiddenIndices.includes(i);
        return <LetterTile key={i} char={ch} hidden={hidden} />;
      })}
    </div>
  );
}

function LetterTile({ char, hidden }: { char: string; hidden: boolean }) {
  return (
    <div
      style={{
        position: 'relative',
        width: 70,
        height: 86,
        display: 'grid',
        placeItems: 'center',
      }}
    >
      {/* diamond accent under the tile */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: hidden
            ? 'rgba(255,255,255,0.04)'
            : `linear-gradient(180deg, ${PALETTE.accent}, ${PALETTE.accentDeep})`,
          border: `2px solid ${hidden ? 'rgba(255,255,255,0.18)' : PALETTE.accentLight}`,
          borderRadius: 14,
          boxShadow: hidden
            ? 'none'
            : `0 8px 24px ${PALETTE.accentGlow}, inset 0 1px 0 rgba(255,255,255,0.18)`,
          opacity: hidden ? 0.55 : 1,
          animation: hidden ? 'cw-tile-pulse 1.8s ease-in-out infinite' : undefined,
        }}
      />
      <span
        style={{
          position: 'relative',
          fontSize: 40,
          fontWeight: 900,
          fontFamily: '"Orbitron",sans-serif',
          color: hidden ? 'rgba(255,255,255,0.35)' : '#001218',
          textShadow: hidden ? 'none' : '0 2px 6px rgba(255,255,255,0.18)',
        }}
      >
        {hidden ? '?' : char.toUpperCase()}
      </span>
    </div>
  );
}

/* ───────────────────── slot grid ───────────────────── */

function SlotsArea({
  grouped,
  showFirstLetters,
}: {
  grouped: Array<[number, TargetWord[]]>;
  showFirstLetters: boolean;
}) {
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: '8px 4px',
      }}
    >
      {grouped.map(([len, words]) => (
        <div key={len} style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
          <div style={{ minWidth: 72 }}>
            <div
              style={{
                fontSize: 28,
                fontWeight: 900,
                color: PALETTE.accentLight,
                lineHeight: 1,
                fontFamily: '"Orbitron",sans-serif',
                textShadow: `0 0 18px ${PALETTE.accentGlow}`,
              }}
            >
              {len}
            </div>
            <div
              style={{
                fontSize: 10,
                letterSpacing: 2,
                color: PALETTE.textDim,
                fontFamily: '"Orbitron",sans-serif',
              }}
            >
              LETTERS
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1 }}>
            {words.map((t) => (
              <Slot key={t.word} target={t} showFirstLetter={showFirstLetters} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Slot({ target, showFirstLetter }: { target: TargetWord; showFirstLetter: boolean }) {
  const found = target.foundBy !== null;
  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '6px 10px',
        borderRadius: 10,
        background: found
          ? 'linear-gradient(180deg, rgba(34,197,94,0.22), rgba(20,83,45,0.45))'
          : 'linear-gradient(180deg, rgba(6,182,212,0.10), rgba(2,6,23,0.55))',
        border: `1.5px solid ${found ? PALETTE.green : PALETTE.accentBorderSoft}`,
        boxShadow: found ? `0 0 22px -8px ${PALETTE.green}` : undefined,
      }}
      title={found ? `${target.foundBy} +${target.points}` : undefined}
    >
      <div style={{ display: 'flex', gap: 3 }}>
        {(found ? target.word.toUpperCase() : '_'.repeat(target.length)).split('').map((ch, i) => {
          const isFirstHint = !found && showFirstLetter && i === 0;
          return (
            <span
              key={i}
              style={{
                minWidth: 18,
                height: 22,
                display: 'grid',
                placeItems: 'center',
                fontFamily: '"JetBrains Mono",monospace',
                fontSize: 14,
                fontWeight: 900,
                color: found ? '#bbf7d0' : isFirstHint ? PALETTE.accentLight : PALETTE.textDim,
                background: found
                  ? 'rgba(34,197,94,0.35)'
                  : isFirstHint
                    ? `rgba(6,182,212,0.22)`
                    : 'rgba(255,255,255,0.04)',
                borderRadius: 4,
              }}
            >
              {isFirstHint ? target.word[0]?.toUpperCase() : found ? ch : ''}
            </span>
          );
        })}
      </div>
      {found && (
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 9 }}>
          <span style={{ color: '#6ee7b7', fontWeight: 800 }}>{target.foundBy}</span>
          <span style={{ color: '#34d399', fontFamily: '"JetBrains Mono",monospace' }}>
            +{target.points}
          </span>
        </div>
      )}
    </div>
  );
}

/* ───────────────────── footer ───────────────────── */

function Footer({ sectionEndsAt }: { sectionEndsAt: number; levelEndsAt: number }) {
  const [rem, setRem] = useState(() => Math.max(0, Math.ceil((sectionEndsAt - Date.now()) / 1000)));
  useEffect(() => {
    const tick = () => setRem(Math.max(0, Math.ceil((sectionEndsAt - Date.now()) / 1000)));
    tick();
    const t = setInterval(tick, 500);
    return () => clearInterval(t);
  }, [sectionEndsAt]);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)',
          borderRadius: 10,
        }}
      >
        <span style={{ fontSize: 16 }}>🔒</span>
        <div>
          <div
            style={{
              fontSize: 9,
              letterSpacing: 2,
              color: PALETTE.textDim,
              fontFamily: '"Orbitron",sans-serif',
            }}
          >
            NEXT ROUND
          </div>
          <div
            style={{
              fontFamily: '"JetBrains Mono",monospace',
              fontSize: 16,
              fontWeight: 900,
              color: PALETTE.text,
            }}
          >
            {rem}s
          </div>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────── right rail (top chatters) ───────────────────── */

function TopChattersRail({ top }: { top: Array<[string, number]> }) {
  return (
    <aside
      style={{
        gridColumn: '2 / 3',
        gridRow: '2 / 3',
        position: 'relative',
        zIndex: 1,
        padding: '20px 18px',
        background: 'linear-gradient(180deg, rgba(11,32,48,0.85), rgba(2,6,23,0.95))',
        border: `2px solid ${PALETTE.accentBorderSoft}`,
        borderRadius: 22,
        boxShadow: '0 20px 56px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)',
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        animation: 'cw-rise 500ms ease both',
      }}
    >
      <div
        style={{
          fontSize: 12,
          letterSpacing: 4,
          color: PALETTE.accentLight,
          textAlign: 'center',
          textTransform: 'uppercase',
          fontWeight: 800,
          marginBottom: 4,
        }}
      >
        Top Chatters
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {top.length === 0 ? (
          <div
            style={{
              fontSize: 12,
              color: 'rgba(255,255,255,0.35)',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: 12,
            }}
          >
            No one scored yet
          </div>
        ) : (
          top.map(([name, score], i) => (
            <div
              key={name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '8px 12px',
                borderRadius: 10,
                background:
                  i === 0
                    ? 'linear-gradient(90deg, rgba(6,182,212,0.30), rgba(255,255,255,0.04))'
                    : 'rgba(255,255,255,0.03)',
                border: i === 0 ? `1px solid ${PALETTE.accent}` : '1px solid transparent',
                boxShadow: i === 0 ? `0 0 22px -10px ${PALETTE.accentGlow}` : undefined,
                fontSize: 13,
              }}
            >
              <span
                style={{
                  fontFamily: '"JetBrains Mono",monospace',
                  fontWeight: 900,
                  color: PALETTE.accentLight,
                  minWidth: 18,
                }}
              >
                {i + 1}
              </span>
              <span style={{ flex: 1, color: PALETTE.text }}>{name}</span>
              <span
                style={{
                  fontFamily: '"JetBrains Mono",monospace',
                  fontWeight: 900,
                  color: PALETTE.text,
                }}
              >
                {score}
              </span>
            </div>
          ))
        )}
      </div>
    </aside>
  );
}

/* ───────────────────── find banner ───────────────────── */

function FindBanner({ event }: { event: ChatWordsLastFind }) {
  return (
    <div
      key={event.ts}
      style={{
        position: 'absolute',
        top: '35%',
        left: '50%',
        zIndex: 8,
        transform: 'translate(-50%, -50%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 4,
        padding: '16px 32px',
        background: 'linear-gradient(135deg, rgba(34,197,94,0.35), rgba(6,182,212,0.25))',
        border: `2px solid ${PALETTE.green}`,
        borderRadius: 20,
        boxShadow: `0 0 72px -10px rgba(52,211,153,0.85)`,
        pointerEvents: 'none',
        animation:
          'cw-banner-in 0.35s cubic-bezier(0.16,1,0.3,1) both, cw-banner-out 0.4s ease-in 1.8s both',
      }}
    >
      <div
        style={{
          fontFamily: '"Orbitron",sans-serif',
          fontSize: 14,
          fontWeight: 800,
          letterSpacing: 2,
          color: '#d1fae5',
        }}
      >
        {event.viewer}
      </div>
      <div
        style={{
          fontFamily: '"Orbitron",sans-serif',
          fontSize: 40,
          fontWeight: 900,
          letterSpacing: 3,
          color: PALETTE.text,
          textShadow: '0 0 24px rgba(255,255,255,0.4)',
        }}
      >
        {event.word.toUpperCase()}
      </div>
      <div
        style={{
          fontFamily: '"JetBrains Mono",monospace',
          fontSize: 22,
          fontWeight: 900,
          color: '#34d399',
          textShadow: '0 0 18px rgba(52,211,153,0.85)',
        }}
      >
        +{event.points}
      </div>
    </div>
  );
}

/* ───────────────────── level reveal / end ───────────────────── */

function LevelReveal({
  lastLevel,
  nextLevel,
}: {
  lastLevel: { level: number; stars: number; score: number; total: number };
  nextLevel: number;
}) {
  return (
    <div
      style={{
        flex: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 22,
        padding: 40,
        background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
        color: PALETTE.text,
        fontFamily: '"Rajdhani","Orbitron",sans-serif',
      }}
    >
      <style>{ANIMATIONS}</style>
      <div
        style={{
          fontFamily: '"Orbitron",sans-serif',
          fontSize: 14,
          letterSpacing: 4,
          textTransform: 'uppercase',
          color: PALETTE.accentLight,
        }}
      >
        Level {lastLevel.level} cleared
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            style={{
              fontSize: 64,
              color: n <= lastLevel.stars ? '#fbbf24' : 'rgba(255,255,255,0.15)',
              textShadow: n <= lastLevel.stars ? '0 0 32px rgba(251,191,36,0.9)' : undefined,
              animation: 'cw-star-pop 0.5s cubic-bezier(0.34,1.56,0.64,1) both',
              animationDelay: `${n * 180}ms`,
              opacity: 0,
            }}
          >
            ★
          </span>
        ))}
      </div>
      <div
        style={{
          fontFamily: '"JetBrains Mono",monospace',
          fontSize: 30,
          fontWeight: 900,
          color: PALETTE.text,
        }}
      >
        {lastLevel.score} / {lastLevel.total} pts
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
        <span style={{ fontSize: 12, letterSpacing: 3, color: PALETTE.textDim }}>NEXT</span>
        <span
          style={{
            fontFamily: '"Orbitron",sans-serif',
            fontSize: 24,
            fontWeight: 900,
            color: PALETTE.accentLight,
            textShadow: `0 0 14px ${PALETTE.accentGlow}`,
          }}
        >
          Level {nextLevel}
        </span>
      </div>
    </div>
  );
}

function Finished({ state, top }: { state: ChatWordsState; top: Array<[string, number]> }) {
  const won = state.levelScore >= state.levelGoalPoints;
  const stars =
    state.levelScore < state.levelGoalPoints
      ? 0
      : state.levelScore >= state.levelTotalPoints
        ? 5
        : state.levelScore >=
            state.levelGoalPoints + (state.levelTotalPoints - state.levelGoalPoints) * 0.67
          ? 3
          : state.levelScore >=
              state.levelGoalPoints + (state.levelTotalPoints - state.levelGoalPoints) * 0.34
            ? 2
            : 1;
  return (
    <div
      style={{
        flex: 1,
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 20,
        padding: 40,
        background: `radial-gradient(ellipse at 50% 20%, ${PALETTE.bgInner} 0%, ${PALETTE.bg} 60%, #000 100%)`,
        color: PALETTE.text,
        fontFamily: '"Rajdhani","Orbitron",sans-serif',
      }}
    >
      <style>{ANIMATIONS}</style>
      <div
        style={{
          fontFamily: '"Orbitron",sans-serif',
          fontSize: 14,
          letterSpacing: 4,
          textTransform: 'uppercase',
          color: PALETTE.accentLight,
        }}
      >
        Level {state.level} complete
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            key={n}
            style={{
              fontSize: 36,
              color: n <= stars ? '#fbbf24' : 'rgba(255,255,255,0.15)',
              textShadow: n <= stars ? '0 0 22px rgba(251,191,36,0.85)' : undefined,
            }}
          >
            ★
          </span>
        ))}
      </div>
      <div
        style={{
          fontFamily: '"Orbitron",sans-serif',
          fontSize: 38,
          fontWeight: 900,
          color: PALETTE.text,
          textShadow: `0 0 24px ${PALETTE.accentGlow}`,
        }}
      >
        {won ? `${state.levelScore} / ${state.levelTotalPoints} pts` : 'Goal missed'}
      </div>
      <div style={{ fontSize: 14, color: PALETTE.textDim }}>
        {state.found.length} of {state.targetWords.length} words found
      </div>
      {top.length > 0 && (
        <div
          style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 360 }}
        >
          {top.map(([name, score], i) => (
            <div
              key={name}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                padding: '10px 16px',
                borderRadius: 10,
                background:
                  i === 0
                    ? `linear-gradient(90deg, ${PALETTE.accentBorder}, rgba(255,255,255,0.04))`
                    : 'rgba(255,255,255,0.04)',
                border: i === 0 ? `1px solid ${PALETTE.accent}` : '1px solid transparent',
                boxShadow: i === 0 ? `0 0 26px -10px ${PALETTE.accentGlow}` : undefined,
                fontSize: 15,
              }}
            >
              <span
                style={{
                  fontFamily: '"JetBrains Mono",monospace',
                  fontWeight: 900,
                  color: PALETTE.accentLight,
                  minWidth: 28,
                }}
              >
                #{i + 1}
              </span>
              <span style={{ flex: 1, color: PALETTE.text }}>{name}</span>
              <span
                style={{
                  fontFamily: '"JetBrains Mono",monospace',
                  fontWeight: 900,
                  color: PALETTE.text,
                }}
              >
                {score}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ───────────────────── helpers ───────────────────── */

function groupByLength(words: TargetWord[]): Array<[number, TargetWord[]]> {
  const m = new Map<number, TargetWord[]>();
  for (const w of words) {
    const a = m.get(w.length) ?? [];
    a.push(w);
    m.set(w.length, a);
  }
  return [...m.entries()].sort((a, b) => a[0] - b[0]);
}

function topScores(scores: Record<string, number>, n: number): Array<[string, number]> {
  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, n);
}
